import { createContext, useContext, useState, ReactNode } from "react";
import { Snackbar, Alert, SxProps, Theme, useTheme } from "@mui/material";

type ToastType = "success" | "error" | "warning" | "info";

interface ToastMessage {
  id: string;
  message: string;
  type: ToastType;
  autoHideDuration?: number;
  sx?: SxProps<Theme>;
}

interface ToastOptions {
  duration?: number;
  sx?: SxProps<Theme>;
}

interface ToastContextType {
  showToast: (message: string, type: ToastType, options?: ToastOptions) => void;
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  warning: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
  closeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

interface ToastProviderProps {
  children: ReactNode;
  defaultDuration?: number;
  position?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
  maxToasts?: number;
}

export function ToastProvider({
  children,
  defaultDuration = 5000,
  position = { vertical: "bottom", horizontal: "right" },
  maxToasts = 3,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const theme = useTheme();

  const showToast = (
    message: string,
    type: ToastType,
    options?: ToastOptions,
  ) => {
    const id = Date.now().toString();
    setToasts((prevToasts) => {
      // Remove oldest toasts if we exceed maxToasts
      const newToasts = [
        ...prevToasts,
        {
          id,
          message,
          type,
          autoHideDuration: options?.duration || defaultDuration,
          sx: options?.sx,
        },
      ];

      if (newToasts.length > maxToasts) {
        return newToasts.slice(newToasts.length - maxToasts);
      }

      return newToasts;
    });
  };

  const success = (message: string, options?: ToastOptions) =>
    showToast(message, "success", options);

  const error = (message: string, options?: ToastOptions) =>
    showToast(message, "error", options);

  const warning = (message: string, options?: ToastOptions) =>
    showToast(message, "warning", options);

  const info = (message: string, options?: ToastOptions) =>
    showToast(message, "info", options);

  const closeToast = (id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  // Get alert styles based on type
  const getAlertSx = (_type: ToastType, customSx?: SxProps<Theme>) => {
    const baseStyles: SxProps<Theme> = {
      width: "100%",
      boxShadow: theme.shadows[3],
    };

    // Merge with custom styles
    return {
      ...baseStyles,
      ...(customSx || {}),
    };
  };

  return (
    <ToastContext.Provider
      value={{
        showToast,
        success,
        error,
        warning,
        info,
        closeToast,
      }}
    >
      {children}
      {toasts.map((toast) => (
        <Snackbar
          key={toast.id}
          open={true}
          autoHideDuration={toast.autoHideDuration}
          onClose={(_event, reason) => {
            if (reason !== "clickaway") {
              closeToast(toast.id);
            }
          }}
          sx={{
            "& + &": { mt: 2 },
          }}
          anchorOrigin={position}
        >
          <Alert
            onClose={() => closeToast(toast.id)}
            severity={toast.type}
            variant="filled"
            elevation={6}
            sx={getAlertSx(toast.type, toast.sx)}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
}
